import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { WhatsappVoiceCampaignService } from 'src/app/private/services/whatsapp-voice-campaign.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

interface CallAnalyticsObject {
  key: string;
  last_modified: string;
  size: number;
  analytics: {
    picked_up: boolean;
    answered_at: string | null;
    ended_at: string | null;
    phone_number: string;
    customer_name: string | null;
    campaign_id: number;
    client_id: string;
    final_stage: string | null;
    collected: any;
    booking_queued: boolean;
    callback_scheduled: boolean;
    callback_date: string | null;
    callback_time: string | null;
    meeting_date: string | null;
    meeting_time: string | null;
    sentiment: string;
    call_active: boolean;
    ended_naturally: boolean;
    lead_id: string | null;
    centre_id: string | null;
    contact_name: string | null;
    email: string | null;
    timezone: string;
    mode: string;
    platform_type: string;
  };
}

interface CallCampaignStats {
  total_calls: number;
  picked_up: number;
  declined: number;
  callback_scheduled: number;
  meeting_booked: number;
  sentiment_positive: number;
  sentiment_negative: number;
  sentiment_callback: number;
  sentiment_unknown: number;
}

interface StatCard {
  label: string;
  value: number;
  percentage: number;
  icon: string;
  color: string;
  trend?: 'up' | 'down' | 'neutral';
}

@Component({
  selector: 'app-call-campaign-analytics',
  templateUrl: './call-campaign-analytics.component.html',
  styleUrls: ['./call-campaign-analytics.component.scss']
})
export class CallCampaignAnalyticsComponent implements OnInit {
  isLoading = true;
  campaignId: string = '';
  
  // Data
  analyticsData: any = null;
  stats: CallCampaignStats | null = null;
  objects: CallAnalyticsObject[] = [];
  
  // Computed Stats
  statCards: StatCard[] = [];
  
  // Filters (Client side search/filter if needed, or simple type selection)
  selectedSentiment: string = 'all';
  sentiments: string[] = ['all', 'positive', 'negative', 'callback', 'unknown'];
  
  // Pagination (Server-side)
  currentPage = 1;
  itemsPerPage = 20;
  totalPages = 1;
  totalItems = 0;

  // Charts
  funnelChartInstance: any = null;
  donutChartInstance: any = null;

  // View Mode
  viewMode: 'table' | 'graph' = 'table';

  constructor(
    private voiceService: WhatsappVoiceCampaignService,
    private localStorageService: LocalStorageService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.campaignId = this.route.snapshot.params['id'];
    this.getCampaignTracking();
  }

  getCampaignTracking() {
    this.isLoading = true;
    const clientId = this.localStorageService.getClientId();

    this.voiceService.getCallAnalytics(clientId, this.campaignId, this.currentPage, this.itemsPerPage).subscribe({
      next: (res: any) => {
        console.log('Call Campaign Analytics:', res);
        this.analyticsData = res;
        this.stats = res.stats;
        this.objects = res.objects || [];
        this.totalItems = res.total || 0;
        this.totalPages = res.pages || 1;
        
        this.calculateStatCards();
        this.isLoading = false;

        // Initialize Charts if we are in graph view
        if (this.viewMode === 'graph') {
          setTimeout(() => {
            this.initAllCharts();
          }, 100);
        }
      },
      error: (err) => {
        console.error('Error fetching Call Campaign Analytics:', err);
        this.isLoading = false;
      }
    });
  }

  get pickupRate(): number {
    const total = this.stats?.total_calls || 0;
    const picked = this.stats?.picked_up || 0;
    if (total <= 0) return 0;
    const rate = (picked / total) * 100;
    return isNaN(rate) || !isFinite(rate) ? 0 : Math.min(100, Math.max(0, rate));
  }

  get meetingRate(): number {
    const picked = this.stats?.picked_up || 0;
    const meeting = this.stats?.meeting_booked || 0;
    if (picked <= 0) return 0;
    const rate = (meeting / picked) * 100;
    return isNaN(rate) || !isFinite(rate) ? 0 : Math.min(100, Math.max(0, rate));
  }

  get callbackRate(): number {
    const picked = this.stats?.picked_up || 0;
    const callback = this.stats?.callback_scheduled || 0;
    if (picked <= 0) return 0;
    const rate = (callback / picked) * 100;
    return isNaN(rate) || !isFinite(rate) ? 0 : Math.min(100, Math.max(0, rate));
  }

  calculateStatCards() {
    if (!this.stats) return;

    const total = this.stats.total_calls || 0;
    const pickedUp = this.stats.picked_up || 0;
    const declined = this.stats.declined || 0;
    const meetingBooked = this.stats.meeting_booked || 0;
    const callbackScheduled = this.stats.callback_scheduled || 0;

    const safePct = (val: number, base: number) => base > 0 ? (val / base) * 100 : 0;

    this.statCards = [
      {
        label: 'Total Calls',
        value: total,
        percentage: 100,
        icon: 'bi-telephone-fill',
        color: '#059669', // Voice bot emerald green
        trend: 'neutral'
      },
      {
        label: 'Picked Up',
        value: pickedUp,
        percentage: safePct(pickedUp, total),
        icon: 'bi-telephone-inbound-fill',
        color: '#10b981', // Success green
        trend: 'up'
      },
      {
        label: 'Declined / Missed',
        value: declined,
        percentage: safePct(declined, total),
        icon: 'bi-telephone-x-fill',
        color: '#ef4444', // Danger red
        trend: 'down'
      },
      {
        label: 'Meeting Booked',
        value: meetingBooked,
        percentage: safePct(meetingBooked, pickedUp),
        icon: 'bi-calendar-check-fill',
        color: '#3b82f6', // Info blue
        trend: 'up'
      },
      {
        label: 'Callback Scheduled',
        value: callbackScheduled,
        percentage: safePct(callbackScheduled, pickedUp),
        icon: 'bi-calendar-event-fill',
        color: '#f59e0b', // Warning yellow
        trend: 'up'
      }
    ];
  }

  initAllCharts() {
    if (!this.stats) return;
    this.initFunnelChart();
    this.initDonutChart();
  }

  initFunnelChart() {
    if (this.funnelChartInstance) this.funnelChartInstance.destroy();

    const canvas = document.getElementById('funnelChart') as HTMLCanvasElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    this.funnelChartInstance = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: ['Total Calls', 'Picked Up', 'Meeting Booked', 'Callback Scheduled'],
        datasets: [{
          label: 'Calls Count',
          data: [
            this.stats?.total_calls || 0,
            this.stats?.picked_up || 0,
            this.stats?.meeting_booked || 0,
            this.stats?.callback_scheduled || 0
          ],
          backgroundColor: [
            'rgba(5, 150, 105, 0.8)',   // Emerald
            'rgba(16, 185, 129, 0.8)',  // Picked up
            'rgba(59, 130, 246, 0.8)',  // Meeting booked
            'rgba(245, 158, 11, 0.8)'   // Callback scheduled
          ],
          borderRadius: 4,
          barPercentage: 0.6
        }]
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        plugins: {
          legend: { display: false },
          title: { display: true, text: 'Call Conversion Funnel', padding: 20 }
        },
        scales: {
          x: { display: false },
          y: { grid: { display: false } }
        }
      }
    });
  }

  initDonutChart() {
    if (this.donutChartInstance) this.donutChartInstance.destroy();

    const canvas = document.getElementById('donutChart') as HTMLCanvasElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const sentimentPositive = this.stats?.sentiment_positive || 0;
    const sentimentNegative = this.stats?.sentiment_negative || 0;
    const sentimentCallback = this.stats?.sentiment_callback || 0;
    const sentimentUnknown = this.stats?.sentiment_unknown || 0;

    this.donutChartInstance = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: ['Positive', 'Negative', 'Callback Request', 'Unknown'],
        datasets: [{
          data: [
            sentimentPositive,
            sentimentNegative,
            sentimentCallback,
            sentimentUnknown
          ],
          backgroundColor: ['#10b981', '#ef4444', '#f59e0b', '#64748b'],
          hoverOffset: 4
        }]
      },
      options: {
        responsive: true,
        cutout: '70%',
        plugins: {
          legend: { position: 'right' },
          title: { display: true, text: 'Call Sentiment Distribution', padding: 20 }
        }
      }
    });
  }

  setViewMode(mode: 'table' | 'graph') {
    this.viewMode = mode;
    if (mode === 'graph') {
      setTimeout(() => {
        this.initAllCharts();
      }, 50);
    }
  }

  // Client-side sentiment filter for current objects list
  get filteredObjects(): CallAnalyticsObject[] {
    if (this.selectedSentiment === 'all') {
      return this.objects;
    }
    return this.objects.filter(obj => 
      (obj.analytics?.sentiment || 'unknown').toLowerCase() === this.selectedSentiment.toLowerCase()
    );
  }

  setSentimentFilter(sentiment: string) {
    this.selectedSentiment = sentiment;
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.getCampaignTracking();
    }
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.getCampaignTracking();
    }
  }

  goToPage(page: number) {
    this.currentPage = page;
    this.getCampaignTracking();
  }

  getStatusBadgeClass(pickedUp: boolean | undefined): string {
    return pickedUp ? 'badge-success' : 'badge-danger';
  }

  getSentimentBadgeClass(sentiment: string | undefined): string {
    const s = (sentiment || '').toLowerCase();
    if (s === 'positive') return 'badge-success';
    if (s === 'negative') return 'badge-danger';
    if (s === 'callback') return 'badge-info';
    return 'badge-secondary';
  }

  formatPhoneNumber(phone: string | undefined): string {
    if (!phone) return '—';
    if (phone.length === 10) {
      return `+91 ${phone.substring(0, 5)} ${phone.substring(5)}`;
    }
    if (phone.startsWith('91') && phone.length === 12) {
      return `+91 ${phone.substring(2, 7)} ${phone.substring(7)}`;
    }
    return phone;
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatPercentage(value: number | null | undefined): string {
    if (value == null || isNaN(value) || !isFinite(value)) {
      return '0.0';
    }
    return Math.min(100, Math.max(0, value)).toFixed(1);
  }

  getCallDuration(start: string | null | undefined, end: string | null | undefined): string {
    if (!start || !end) return '—';
    const startTime = new Date(start).getTime();
    const endTime = new Date(end).getTime();
    const durationMs = endTime - startTime;
    if (isNaN(durationMs) || durationMs <= 0) return '0s';
    
    const totalSeconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    
    return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
  }

  goBack() {
    this.router.navigate(['/main/broadcast-campaigns']);
  }

  refreshData() {
    this.getCampaignTracking();
  }

  exportData() {
    const dataStr = JSON.stringify(this.analyticsData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `call-campaign-${this.campaignId}-analytics.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  hasCollectedData(collected: any): boolean {
    if (!collected) return false;
    return Object.keys(collected).length > 0;
  }

  formatKey(key: string | number | unknown): string {
    const normalized = String(key ?? '');
    if (!normalized) return '';
    return normalized.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }
}
